import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AICoachingMessage, LiveUtterance, TranscriptEntry, TysonCoachingContextData } from '../types/coaching';
import { useTysonRealtimeCoaching } from '../hooks/useTysonRealtimeCoaching';

interface TysonCoachingContextValue {
	coachingMessages: AICoachingMessage[];
	conversationId: string | null;
	isCreatingConversation: boolean;
	isAIResponding: boolean;
	isActive: boolean;
	contextData: TysonCoachingContextData | null;
	// Historical conversation viewing
	viewingHistoricalConversationId: string | null;
	viewHistoricalConversation: (conversationId: string) => void;
	clearHistoricalConversation: () => void;
	startCoaching: (contextData: TysonCoachingContextData) => Promise<void>;
	enrichCreationContext: (params: { phoneCallId?: string; leadServedId?: string; source?: string }) => void;
	sendTranscriptEntry: (entry: TranscriptEntry) => void;
	sendTranscriptBatchImmediately: (entries: TranscriptEntry[]) => void;
	sendEndOfCallSummary: () => void;
	sendDirectQuestion: (question: string) => void;
	stopCoaching: () => void;
	// Live utterances for display
	liveUtterances: LiveUtterance[];
	// Set live utterances (called by RealCallCoachingBridge)
	setLiveUtterances: (utterances: LiveUtterance[]) => void;
	// Manual suggestion request (for real calls)
	requestSuggestion: () => void;
	// Register flush callback from real call hook
	registerFlushCallback: (callback: (() => TranscriptEntry[]) | null) => void;
	// Post-call summary state
	callEndedPendingSummary: boolean;
	setCallEndedPendingSummary: (value: boolean) => void;
	isCallConnected: boolean;
	setIsCallConnected: (value: boolean) => void;
	/**
	 * Flush any transcript that hasn't reached the AI yet and ask for the
	 * end-of-call summary. `requireTranscript` is for the automatic call-end
	 * trigger: it skips calls that never produced transcript (dial failure,
	 * instant hangup) and leaves the manual "Get Summary" button on screen
	 * instead. A manual click passes nothing and is always honored.
	 */
	requestPostCallSummary: (options?: { requireTranscript?: boolean }) => void;
	clearPendingTranscriptData: () => void;
	sendNewCallStartedMessage: (contactName: string) => void;
	sendPracticeCallStartedMessage: (contactName: string) => void;
	// Coaching agent template selection (persisted in localStorage)
	selectedCoachingTemplateId: string | null;
	setSelectedCoachingTemplateId: (id: string | null) => void;
}

const TysonCoachingContext = createContext<TysonCoachingContextValue | null>(null);

const COACHING_TEMPLATE_STORAGE_KEY = 'tyson-coaching-template-id';

export const TysonCoachingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [isActive, setIsActive] = useState(false);
	const [contextData, setContextData] = useState<TysonCoachingContextData | null>(null);
	const [viewingHistoricalConversationId, setViewingHistoricalConversationId] = useState<string | null>(null);
	const [liveUtterances, setLiveUtterancesState] = useState<LiveUtterance[]>([]);
	const tysonPendingEntriesRef = useRef<TranscriptEntry[]>([]);
	const flushCallbackRef = useRef<(() => TranscriptEntry[]) | null>(null);
	// Whether the CURRENT call has already pushed a transcript batch to the AI.
	// Lets the auto-fired summary tell "nothing was ever said" (skip) apart from
	// "the last suggestion already consumed everything pending" (still summarize).
	// Scoped to one call: cleared when a summary goes out and on every teardown,
	// because answered inbound calls never pass through `Dialing` and so never hit
	// the bridge's `clearPendingTranscriptData` reset.
	const sentTranscriptRef = useRef(false);
	// Set when the call ends while a stream is still open; drained by the
	// isAIResponding watcher below.
	const deferredSummaryOptionsRef = useRef<{ requireTranscript?: boolean } | null>(null);
	const [callEndedPendingSummary, setCallEndedPendingSummary] = useState(false);
	const [isCallConnected, setIsCallConnected] = useState(false);
	const [selectedCoachingTemplateId, setSelectedCoachingTemplateIdState] = useState<string | null>(() =>
		localStorage.getItem(COACHING_TEMPLATE_STORAGE_KEY)
	);

	const setSelectedCoachingTemplateId = useCallback((id: string | null) => {
		setSelectedCoachingTemplateIdState(id);
		if (id) {
			localStorage.setItem(COACHING_TEMPLATE_STORAGE_KEY, id);
		} else {
			localStorage.removeItem(COACHING_TEMPLATE_STORAGE_KEY);
		}
	}, []);

	const setLiveUtterances = useCallback((utterances: LiveUtterance[]) => {
		setLiveUtterancesState(utterances);
	}, []);

	const coaching = useTysonRealtimeCoaching(contextData);

	const startCoaching = async (data: TysonCoachingContextData) => {
		// Clear any historical conversation view when starting a new session
		setViewingHistoricalConversationId(null);
		// Auto-include the selected coaching template if not explicitly provided
		const dataWithTemplate = {
			...data,
			agentTemplateId: data.agentTemplateId ?? selectedCoachingTemplateId ?? undefined,
		};
		setContextData(dataWithTemplate);
		setIsActive(true);
		// Pass data directly to avoid race condition with async state update
		await coaching.createConversation(dataWithTemplate);
	};

	const stopCoaching = () => {
		if (coaching.coachingMessages.length > 0) {
			console.groupCollapsed('[Coffee] Full Conversation (%d messages)', coaching.coachingMessages.length);
			console.log(JSON.stringify(coaching.coachingMessages, null, 2));
			console.groupEnd();
		}
		setIsActive(false);
		setContextData(null);
		sentTranscriptRef.current = false;
		deferredSummaryOptionsRef.current = null;
		coaching.resetCoaching();
	};

	const viewHistoricalConversation = (conversationId: string) => {
		setViewingHistoricalConversationId(conversationId);
	};

	const clearHistoricalConversation = () => {
		setViewingHistoricalConversationId(null);
	};

	const registerFlushCallback = (callback: (() => TranscriptEntry[]) | null) => {
		flushCallbackRef.current = callback;
	};

	// Wrapper to accumulate entries for manual flush AND display in coaching panel
	const handleSendTranscriptEntry = useCallback(
		(entry: TranscriptEntry) => {
			// Accumulate for sending when "Get Suggestion" is clicked
			tysonPendingEntriesRef.current = [...tysonPendingEntriesRef.current, entry];

			// Add to liveUtterances for display in coaching panel
			const utterance: LiveUtterance = {
				sequenceId: `tyson-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
				speaker: entry.speaker as 'Lead' | 'Sales Rep',
				transcription: entry.message,
				final: true, // Tyson transcripts are always final (complete messages)
				timestamp: entry.timestamp,
			};
			setLiveUtterancesState(prev => [...prev, utterance]);

			// Still queue in hook for backward compat
			coaching.sendTranscriptEntry(entry);
		},
		[coaching]
	);

	const requestSuggestion = () => {
		// For real calls: use the registered flush callback
		if (flushCallbackRef.current) {
			const entries = flushCallbackRef.current();
			if (entries && entries.length > 0) {
				sentTranscriptRef.current = true;
				coaching.sendTranscriptBatchImmediately(entries);
				return;
			}
			// Fall through to Tyson pending entries if real call flush returned nothing
		}

		// For Tyson practice calls (or fallback when real call flush is empty): flush accumulated entries
		if (tysonPendingEntriesRef.current.length > 0) {
			sentTranscriptRef.current = true;
			coaching.sendTranscriptBatchImmediately(tysonPendingEntriesRef.current);
			tysonPendingEntriesRef.current = [];
			setLiveUtterancesState([]); // Clear display after sending
		}
	};

	const requestPostCallSummary = useCallback(
		(options?: { requireTranscript?: boolean }) => {
			if (!coaching.conversationId) return;

			// This pipeline has no send-queue gate (unlike the focused view) and the AI
			// provider rejects a second POST while a stream is open on the same
			// conversation. The lead's last utterance often fires an auto-suggestion a
			// moment before the hangup, so hold the summary until that stream closes.
			if (coaching.isAIResponding) {
				deferredSummaryOptionsRef.current = options ?? {};
				return;
			}

			// For real calls: flush remaining transcript from useRealCallCoaching via the registered callback.
			// For Tyson practice calls: flushCallbackRef is null, fall back to tysonPendingEntries.
			const realCallEntries = flushCallbackRef.current?.() ?? [];
			const allEntries = [...realCallEntries, ...tysonPendingEntriesRef.current];

			if (options?.requireTranscript && allEntries.length === 0 && !sentTranscriptRef.current) {
				setCallEndedPendingSummary(true);
				return;
			}

			if (allEntries.length > 0) {
				sentTranscriptRef.current = true;
				const formattedTranscript = allEntries
					.map(entry => `[${entry.timestamp.toLocaleTimeString()}] ${entry.speaker}: ${entry.message}`)
					.join('\n');
				coaching.sendEndOfCallSummaryWithTranscript(formattedTranscript);
			} else {
				coaching.sendEndOfCallSummary();
			}

			tysonPendingEntriesRef.current = [];
			sentTranscriptRef.current = false;

			setLiveUtterancesState([]);
			setCallEndedPendingSummary(false);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			coaching.conversationId,
			coaching.isAIResponding,
			coaching.sendEndOfCallSummary,
			coaching.sendEndOfCallSummaryWithTranscript,
		]
	);

	// Release a summary that was held back because a stream was still open.
	const wasAIRespondingRef = useRef(false);
	useEffect(() => {
		if (wasAIRespondingRef.current && !coaching.isAIResponding && deferredSummaryOptionsRef.current) {
			const options = deferredSummaryOptionsRef.current;
			deferredSummaryOptionsRef.current = null;
			requestPostCallSummary(options);
		}
		wasAIRespondingRef.current = coaching.isAIResponding;
	}, [coaching.isAIResponding, requestPostCallSummary]);

	const clearPendingTranscriptData = useCallback(() => {
		tysonPendingEntriesRef.current = [];
		sentTranscriptRef.current = false;
		deferredSummaryOptionsRef.current = null;

		setLiveUtterancesState([]);
	}, []);

	const sendNewCallStartedMessage = useCallback(
		(contactName: string) => {
			coaching.sendNextContactStartedAction(contactName);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[coaching.sendNextContactStartedAction]
	);

	const sendPracticeCallStartedMessage = useCallback(
		(contactName: string) => {
			coaching.sendPracticeCallStartedAction(contactName);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[coaching.sendPracticeCallStartedAction]
	);

	const value: TysonCoachingContextValue = {
		coachingMessages: coaching.coachingMessages,
		conversationId: coaching.conversationId,
		isCreatingConversation: coaching.isCreatingConversation,
		isAIResponding: coaching.isAIResponding,
		isActive,
		contextData,
		viewingHistoricalConversationId,
		viewHistoricalConversation,
		clearHistoricalConversation,
		startCoaching,
		enrichCreationContext: coaching.enrichCreationContext,
		sendTranscriptEntry: handleSendTranscriptEntry,
		sendTranscriptBatchImmediately: coaching.sendTranscriptBatchImmediately,
		sendEndOfCallSummary: coaching.sendEndOfCallSummary,
		sendDirectQuestion: coaching.sendDirectQuestion,
		stopCoaching,
		// Live utterances for real call coaching
		liveUtterances,
		setLiveUtterances,
		requestSuggestion,
		registerFlushCallback,
		callEndedPendingSummary,
		setCallEndedPendingSummary,
		isCallConnected,
		setIsCallConnected,
		requestPostCallSummary,
		clearPendingTranscriptData,
		sendNewCallStartedMessage,
		sendPracticeCallStartedMessage,
		selectedCoachingTemplateId,
		setSelectedCoachingTemplateId,
	};

	return <TysonCoachingContext.Provider value={value}>{children}</TysonCoachingContext.Provider>;
};

export const useTysonCoaching = (): TysonCoachingContextValue => {
	const context = useContext(TysonCoachingContext);
	if (!context) {
		throw new Error('useTysonCoaching must be used within a TysonCoachingProvider');
	}
	return context;
};
